import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscribeToPushNotifications } from '../utils/pushNotifications';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Initialize session from storage (localStorage only for persistence)
    useEffect(() => {
        const savedUser = localStorage.getItem('stock_user');
        if (savedUser) {
            try {
                const parsedUser = JSON.parse(savedUser);
                setUser(parsedUser);

                // If permission is already granted, resubscribe to ensure VAPID and token are fresh
                if (parsedUser && parsedUser.id && 'Notification' in window && Notification.permission === 'granted' && !localStorage.getItem('stock_push_disabled')) {
                    subscribeToPushNotifications(parsedUser.id, parsedUser.companyId).catch(console.error);
                }
            } catch (e) {
                localStorage.removeItem('stock_user');
            }
        }
        setLoading(false);
    }, []);

    const login = useCallback((userData, remember = true) => {
        setUser(userData);
        if (userData && 'Notification' in window && !localStorage.getItem('stock_push_disabled')) {
            subscribeToPushNotifications(userData.id, userData.companyId).catch(console.error);
        }
        // Always store in localStorage for offline reliability
        localStorage.setItem('stock_user', JSON.stringify(userData));
    }, []);

    const logout = useCallback(() => {
        setUser(null);
        localStorage.removeItem('stock_user');
        sessionStorage.removeItem('stock_user');
    }, []);

    const hasRole = useCallback((roles) => {
        if (!user) return false;

        const checkRoles = Array.isArray(roles) ? roles : [roles];

        // Get role name from object or string
        const userRoleName = (typeof user.role === 'string' ? user.role : user.role?.name)?.toLowerCase();

        // console.log(`[AuthDebug] Checking roles ${roles} against user role: ${userRoleName}`);

        if (!userRoleName) return false;

        const result = checkRoles.some(r => r.toLowerCase() === userRoleName);
        return result;
    }, [user]);

    const hasPermission = useCallback((permission) => {
        if (!user || !user.role) return false;

        const role = user.role;
        const roleName = (typeof role === 'string' ? role : role.name)?.toLowerCase();

        // If user is Admin, they have all permissions
        if (roleName === 'admin') return true;

        if (!role.permissions) return false;
        return role.permissions.some(p => p.name === permission);
    }, [user]);

    const value = React.useMemo(() => ({
        user, login, logout, hasRole, hasPermission, loading
    }), [user, login, logout, hasRole, hasPermission, loading]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
